import {useState} from "react";
import SubscriptionPlan from "../components/subscriptionPlan/SubscriptionPlan";
import SubscriptionPlanSkeleton from "../components/subscriptionPlan/SubscriptionPlanSkeleton";
import Carousel from "../components/carousel/Carousel";
import {trim} from "../utils/trim";
import {useMediaQuery} from "react-responsive";
import {useSubscriptionDetails} from "../hooks/useSubscriptionDetails";
import {useSubscriptionPrice} from "../hooks/useSubscriptionPrice";
import useAuthPersistStore from "../store/authPersistStore";
import {REFERRAL_PARAM, REFERRAL_SEMINAR} from "../constants/url-params";
import {PlanTypes} from "../types";
import {SUBSCRIPTION_PLANS, ONE_TIME_PLANS} from "../constants/plans";

const subscriptionPlans = SUBSCRIPTION_PLANS;

const oneTimePlans = ONE_TIME_PLANS;

const Subscription = () => {
  const searchParams = new URLSearchParams(document.location.search);
  const referralValue = searchParams.get(REFERRAL_PARAM);
  const isReferralForSeminar = referralValue === REFERRAL_SEMINAR;

  const [activeTab, setActiveTab] = useState<"oneTime" | "subscription">(
    "oneTime"
  );

  const isTablet = useMediaQuery({query: "(min-width: 900px)"});

  const {isAuthenticated} = useAuthPersistStore();
  const {data: subscriptionDetails, isLoading} = useSubscriptionDetails({
    isAuthenticated,
  });

  const {
    data: monthlySubscriptionPrice,
    isLoading: isMonthlySubscriptionLoading,
  } = useSubscriptionPrice(PlanTypes.Monthly);

  const {
    data: sixMonthSubscriptionPrice,
    isLoading: isSixMonthSubscriptionLoading,
  } = useSubscriptionPrice(PlanTypes.SixMonths);

  const {
    data: yearlySubscriptionPrice,
    isLoading: isYearlySubscriptionLoading,
  } = useSubscriptionPrice(PlanTypes.Yearly);

  const {
    data: oneTimeOneMonthSubscriptionPrice,
    isLoading: isOneTimeOneMonthSubscriptionLoading,
  } = useSubscriptionPrice(PlanTypes.OneTimeOneMonth);

  const {
    data: oneTimeThreeMonthsSubscriptionPrice,
    isLoading: isOneTimeThreeMonthsSubscriptionLoading,
  } = useSubscriptionPrice(PlanTypes.OneTimeThreeMonths);

  const {
    data: oneTimeSixMonthsSubscriptionPrice,
    isLoading: isOneTimeSixMonthsSubscriptionLoading,
  } = useSubscriptionPrice(PlanTypes.OneTimeSixMonths);

  const {
    data: oneTimeYearSubscriptionPrice,
    isLoading: isOneTimeYearSubscriptionLoading,
  } = useSubscriptionPrice(PlanTypes.OneTimeYear);

  // loading po tipu plana — da svaka sekcija čeka SAMO svoje cene,
  // a ne sve (jedan spor/404 poziv ne blokira ostale kartice)
  const priceLoadingByType: Record<string, boolean> = {
    [PlanTypes.Monthly]: isMonthlySubscriptionLoading,
    [PlanTypes.SixMonths]: isSixMonthSubscriptionLoading,
    [PlanTypes.Yearly]: isYearlySubscriptionLoading,
    [PlanTypes.OneTimeOneMonth]: isOneTimeOneMonthSubscriptionLoading,
    [PlanTypes.OneTimeThreeMonths]: isOneTimeThreeMonthsSubscriptionLoading,
    [PlanTypes.OneTimeSixMonths]: isOneTimeSixMonthsSubscriptionLoading,
    [PlanTypes.OneTimeYear]: isOneTimeYearSubscriptionLoading,
  };

  const isSectionLoading = (plans: any[]) =>
    (isAuthenticated && isLoading) ||
    plans.some((plan) => priceLoadingByType[plan.subscriptionType]);

  const pricesMap = {
    [PlanTypes.Monthly]: monthlySubscriptionPrice,
    [PlanTypes.SixMonths]:sixMonthSubscriptionPrice,
    [PlanTypes.Yearly]:yearlySubscriptionPrice,
    [PlanTypes.OneTimeOneMonth]: oneTimeOneMonthSubscriptionPrice,
    [PlanTypes.OneTimeThreeMonths]: oneTimeThreeMonthsSubscriptionPrice,
    [PlanTypes.OneTimeSixMonths]: oneTimeSixMonthsSubscriptionPrice,
    [PlanTypes.OneTimeYear]: oneTimeYearSubscriptionPrice,
  };

  // sakrij jednokratni plan koji korisnik trenutno poseduje (ne moze ponovo da ga kupi)
  const ownedType = subscriptionDetails?.subscriptionType;
  const visibleOneTimePlans =
    isAuthenticated && ownedType
      ? oneTimePlans.filter((plan) => plan.subscriptionType !== ownedType)
      : oneTimePlans;

  const renderPlanItem = (item: any) => {
    return (
      // @ts-ignore
      <SubscriptionPlan
        key={`${item.title} - ${item.subscriptionType}`}
        // @ts-ignore
        subscriptionDetails={subscriptionDetails}
        subscriptionPrice={pricesMap[item.subscriptionType as PlanTypes]}
        {...item}
      />
    );
  };

  const renderSection = (plans: any[]) => {
    if (isSectionLoading(plans)) {
      return (
        <div className="subscription_grid">
          {plans.map((_, i) => (
            <SubscriptionPlanSkeleton key={i} />
          ))}
        </div>
      );
    }

    return isTablet ? (
      <div className="subscription_grid">{plans.map(renderPlanItem)}</div>
    ) : (
      <div className="subscription_carousel">
        <Carousel
          items={plans}
          // @ts-ignore
          component={renderPlanItem}
          options={{
            gap: 16,
            padding: 16,
            perMove: 1,
            perPage: 1,
          }}
        />
      </div>
    );
  };

  return (
    <div className="subscription">
      <div className="subscription_header">
        <h1>
          {subscriptionDetails?.subscriptionType
            ? "Opcije pretplate"
            : "Odaberi plan"}
        </h1>
      </div>

      <div className="subscription_tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "oneTime"}
          className={trim(
            `subscription_tab ${
              activeTab === "oneTime" ? "subscription_tab--active" : ""
            }`
          )}
          onClick={() => setActiveTab("oneTime")}
        >
          Jednokratno plaćanje
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "subscription"}
          className={trim(
            `subscription_tab ${
              activeTab === "subscription" ? "subscription_tab--active" : ""
            }`
          )}
          onClick={() => setActiveTab("subscription")}
        >
          Pretplata
        </button>
      </div>

      <div className="subscription_section">
        {activeTab === "oneTime" ? (
          <>
            <div className="subscription_section-header">
              <p>Plati jednom, bez automatskog obnavljanja.</p>
            </div>
            {renderSection(visibleOneTimePlans)}
          </>
        ) : (
          <>
            <div className="subscription_section-header">
              <p>Automatsko obnavljanje, otkaži bilo kada.</p>
            </div>
            {renderSection(subscriptionPlans)}
          </>
        )}
      </div>
    </div>
  );
};

Subscription.propTypes = {};

export default Subscription;
